import { FloatingOverlay } from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { FC, Fragment, useEffect } from 'react';
import Modal from './Modal';
import { requestModalClose } from './requestModalClose';
import useModalManagerStore from './useModalManagerStore';

const ModalManager: FC<React.PropsWithChildren<React.PropsWithChildren<unknown>>> = () => {
    const modals = useModalManagerStore((state) => state.modals);
    const resolveZIndex = (index: number) => 10000 + index * 10;

    // Close the topmost modal on Escape — one modal per keypress, leaving the rest open.
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            // Let a nested handler (e.g. an inline dropdown) that already consumed Escape win.
            if (e.key !== 'Escape' || e.defaultPrevented) return;
            // Read the live stack so the listener can stay registered for the component's lifetime.
            const { modals: openModals } = useModalManagerStore.getState();
            if (openModals.length === 0) return;
            // Topmost modal = latest createdAt, matching the render order's z-index stacking.
            const topmost = openModals.reduce((a, b) => (a.createdAt?.getTime() > b.createdAt?.getTime() ? a : b));
            // A blocking modal stays in control; never reach the modals underneath it.
            if (topmost.preventClose) return;
            // One Escape closes exactly this modal — mirror the X button, then consume the event.
            requestModalClose(topmost);
            e.preventDefault();
            e.stopPropagation();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    const ordered = [...modals].sort((a, b) => a.createdAt?.getTime() - b.createdAt?.getTime());

    return (
        <AnimatePresence>
            {ordered.map((modal, index) => (
                <Fragment key={modal.id}>
                    {/*
                          Modal Backdrop — tinted and blurred only on the topmost one.

                          Every modal used to draw its own, which is invisible while
                          there is one and unmistakable the moment there are two: a
                          confirm opened over a settings dialog put a second white
                          veil and a second blur over the first, so the page behind
                          went noticeably paler and softer with each layer and the
                          stack read as heavier than it is. The ones underneath keep
                          their box — it positions the modal and holds the scroll
                          lock — and give up only the paint, which nobody can see
                          through the dialog on top of them anyway.
                        */}
                    <motion.div
                        transition={{
                            duration: 0.2,
                            type: 'spring'
                        }}
                        initial={{ opacity: 0 }}
                        exit={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={
                            index === ordered.length - 1
                                ? 'tw-fixed tw-inset-0 tw-bg-white/30 tw-backdrop-blur-lg'
                                : 'tw-fixed tw-inset-0'
                        }
                        style={{ zIndex: resolveZIndex(index) }}
                    >
                        <FloatingOverlay lockScroll />
                        <Modal {...modal} style={{ zIndex: resolveZIndex(index) + 5 }} />
                    </motion.div>
                </Fragment>
            ))}
        </AnimatePresence>
    );
};

export default ModalManager;
